import type {ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {Pill} from '@Components/pill/pill';
import {PillSize} from '@Components/pill/pill.types';
import {Text, TextSize, TextTag} from '@Components/text';
import type {AiContentVerificationResult} from '@Libraries/ai-content-verification.types';
import {getIntlLocale} from '../../ai-content-verify.library';
import {AiContentVerifyFilePreview} from '../ai-content-verify-file-preview/ai-content-verify-file-preview';
import type {AiContentVerifyResultDetail, AiContentVerifyResultProps, AiContentVerifyVerdictCopy} from './ai-content-verify-result.types';
import {AI_GENERATED_VERDICT, DIGITAL_SOURCE_TYPE_LABEL_KEYS, MARKED_ON_DATE_FORMAT, MEDIA_KIND_LABEL_KEYS, NO_MARKS_VERDICT, RESULT_HEADING_ID} from './ai-content-verify-result.types';
import styles from './ai-content-verify-result.module.scss';

export function AiContentVerifyResult({result, file, onCheckAnotherFile}: AiContentVerifyResultProps): ReactElement {
  const headingRef = useRef<HTMLDivElement>(null);
  const verdict = getVerdictCopy(result.isAiGenerated);

  useEffect(() => {
    headingRef.current?.focus();
  }, [result]);

  return (
    <section aria-labelledby={RESULT_HEADING_ID} className={styles.result}>
      <div ref={headingRef} tabIndex={-1} className={styles.resultVerdictText}>
        <Pill label={window.i18next.t(verdict.pillLabelKey)} type={verdict.pillType} icon={verdict.pillIcon} size={PillSize.SM} />
        <Text val={window.i18next.t(verdict.titleKey)} tag={TextTag.H2} size={TextSize.LG} bold attributes={{id: RESULT_HEADING_ID}} className={styles.resultVerdictTitle} />
        <Text val={window.i18next.t(verdict.descriptionKey)} size={TextSize.MD} className={styles.resultVerdictDescription} />
      </div>
      <AiContentVerifyFilePreview file={file} />
      <dl className={styles.resultDetails}>
        {getResultDetails(result).map((detail) => {
          return (
            <div key={detail.labelKey} className={styles.resultDetail}>
              <dt className={styles.resultDetailLabel}>
                <Text val={window.i18next.t(detail.labelKey)} size={TextSize.SM} />
              </dt>
              <dd className={styles.resultDetailValue}>
                <Text val={detail.value} size={TextSize.MD} bold />
              </dd>
            </div>
          );
        })}
      </dl>
      <Button
        text={window.i18next.t('pmwjs_ai_verify_check_another')}
        icon="icon-refresh"
        iconPlacement={IconPlacement.LEFT}
        type={Type.SECONDARY}
        size={Size.SMALL}
        onClick={onCheckAnotherFile}
      />
    </section>
  );
}

function getVerdictCopy(isAiGenerated: boolean): AiContentVerifyVerdictCopy {
  if (isAiGenerated) {
    return AI_GENERATED_VERDICT;
  }

  return NO_MARKS_VERDICT;
}

function getResultDetails(result: AiContentVerificationResult): AiContentVerifyResultDetail[] {
  const details: AiContentVerifyResultDetail[] = [
    {labelKey: 'pmwjs_ai_verify_detail_file', value: result.fileName},
    {labelKey: 'pmwjs_ai_verify_detail_type', value: getTranslatedLabel(MEDIA_KIND_LABEL_KEYS, result.mediaKind)},
  ];

  if (result.digitalSourceType) {
    details.push({labelKey: 'pmwjs_ai_verify_detail_source', value: getTranslatedLabel(DIGITAL_SOURCE_TYPE_LABEL_KEYS, result.digitalSourceType)});
  }

  if (result.system) {
    details.push({labelKey: 'pmwjs_ai_verify_detail_system', value: result.system});
  }

  if (result.provenanceId) {
    details.push({labelKey: 'pmwjs_ai_verify_detail_provenance_id', value: result.provenanceId});
  }

  if (result.createdAt) {
    details.push({labelKey: 'pmwjs_ai_verify_detail_created_at', value: formatMarkedOnDate(result.createdAt)});
  }

  return details;
}

function getTranslatedLabel(labelKeys: Record<string, string>, value: string): string {
  const labelKey = labelKeys[value];

  if (!labelKey) {
    return value;
  }

  return window.i18next.t(labelKey);
}

function formatMarkedOnDate(createdAt: string): string {
  const markedOn = new Date(createdAt);

  if (Number.isNaN(markedOn.getTime())) {
    return createdAt;
  }

  return new Intl.DateTimeFormat(getIntlLocale(), MARKED_ON_DATE_FORMAT).format(markedOn);
}
